import * as React from 'react';

export type Theme = 'light' | 'dark';

// Keep in sync with the inline script in public/index.html, which applies the
// saved theme before React mounts so there's no light-mode flash on reload.
export const THEME_STORAGE_KEY = 'bark-theme';
export const THEME_NAME_STORAGE_KEY = 'bark-theme-name';
export const THEME_FX_STORAGE_KEY = 'bark-theme-fx';

export type ThemeName = 'default' | 'fall' | 'thanksgiving' | 'winter' | 'christmas';
// 'seasonal' is a user choice that resolves to a ThemeName based on today's date.
export type ThemeChoice = ThemeName | 'seasonal';

export const THEME_OPTIONS: { value: ThemeChoice; label: string }[] = [
  { value: 'default', label: 'Default' },
  { value: 'seasonal', label: '✨ Seasonal (auto)' },
  { value: 'fall', label: '🍂 Fall' },
  { value: 'thanksgiving', label: '🦃 Thanksgiving' },
  { value: 'winter', label: '❄️ Winter' },
  { value: 'christmas', label: '🎄 Christmas' },
];

// Decorative emojis shown in the nav banner for each theme.
export const THEME_BANNER_EMOJIS: Record<ThemeName, string> = {
  default: '',
  fall: '🍂 🍁 🍂',
  thanksgiving: '🦃 🥧 🌽',
  winter: '❄️ ⛄ ❄️',
  christmas: '🎄 🎁 ⭐',
};

const THEME_CHOICES = THEME_OPTIONS.map((o) => o.value);

// Keep in sync with the inline script in public/index.html.
export const resolveThemeName = (choice: ThemeChoice, now: Date = new Date()): ThemeName => {
  if (choice !== 'seasonal') return choice;
  const month = now.getMonth(); // 0 = Jan
  if (month === 9) return 'fall'; // Oct
  if (month === 10) return 'thanksgiving'; // Nov
  if (month === 11) return 'christmas'; // Dec
  if (month === 0 || month === 1) return 'winter'; // Jan–Feb
  return 'default';
};

// Emojis that drift down the page for each theme (see FallingEmojis).
export const THEME_FALLING_EMOJIS: Record<ThemeName, string[]> = {
  default: [],
  fall: ['🍂', '🍁', '🍂'],
  thanksgiving: ['🍂', '🍁', '🌽'],
  winter: ['❄️', '❄️', '❅'],
  christmas: ['❄️', '⭐', '❄️'],
};

interface ThemeContextValue {
  theme: Theme;
  isDark: boolean;
  setTheme: (theme: Theme) => void;
  themeChoice: ThemeChoice;
  setThemeChoice: (choice: ThemeChoice) => void;
  /** Whether the falling-emoji effect is enabled (only shown for non-default themes). */
  fxEnabled: boolean;
  setFxEnabled: (enabled: boolean) => void;
  toggleTheme: () => void;
}

const ThemeContext = React.createContext<ThemeContextValue | undefined>(undefined);

const readStoredTheme = (): Theme => {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
};

const readStoredThemeChoice = (): ThemeChoice => {
  try {
    const stored = localStorage.getItem(THEME_NAME_STORAGE_KEY) as ThemeChoice | null;
    return stored && THEME_CHOICES.includes(stored) ? stored : 'default';
  } catch {
    return 'default';
  }
};

const readStoredFx = (): boolean => {
  try {
    return localStorage.getItem(THEME_FX_STORAGE_KEY) !== 'off';
  } catch {
    return true;
  }
};

const applyTheme = (theme: Theme, themeName: ThemeName) => {
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  root.style.colorScheme = theme;
  if (themeName === 'default') {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', themeName);
  }
};

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [theme, setThemeState] = React.useState<Theme>(readStoredTheme);
  const [themeChoice, setThemeChoice] = React.useState<ThemeChoice>(readStoredThemeChoice);
  const [fxEnabled, setFxEnabled] = React.useState<boolean>(readStoredFx);

  React.useEffect(() => {
    applyTheme(theme, resolveThemeName(themeChoice));
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
      localStorage.setItem(THEME_NAME_STORAGE_KEY, themeChoice);
      localStorage.setItem(THEME_FX_STORAGE_KEY, fxEnabled ? 'on' : 'off');
    } catch {
      // Storage can be unavailable (e.g. privacy mode); the theme still applies for this session.
    }
  }, [theme, themeChoice, fxEnabled]);

  const value = React.useMemo<ThemeContextValue>(
    () => ({
      theme,
      isDark: theme === 'dark',
      setTheme: setThemeState,
      themeChoice,
      setThemeChoice,
      fxEnabled,
      setFxEnabled,
      toggleTheme: () => setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark')),
    }),
    [theme, themeChoice, fxEnabled]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
